/*
 * Copyright 2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { lazy, Suspense, useRef, useState, useEffect } from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {makeStyles} from '@material-ui/core/styles';

import _ from "lodash";
import Grid from "@material-ui/core/Grid";
import {
    Backdrop,
    Breadcrumbs,
    Card,
    CardContent, CardMedia, CircularProgress,
    Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination,
    TableRow, Tooltip,
    Typography
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import {EventStatsCard} from "./EventStatsCard";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import {APIHandler} from "../../common/APIHandler/APIHandler";
import IconButton from "@material-ui/core/IconButton";
import {ProfileViewDialog} from "../Profile/ProfileViewDialog";
import {Button} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import config from '../../config';
import CustomFilter from '../../components/Filter/filter';
import ReactPlayer from "react-player";


momentDurationFormatSetup(moment);

const CLIPS_LIMIT = 25;

// auto-refresh will be enabled whent the event has these statuses
const EVENT_STATUSES_ENABLE_AUTO_REFRESH = [
    "Queued",
    "In Progress",
    "Paused",
]


const useStyles = makeStyles((theme) => ({
    content: {
        marginTop: 'auto',
        flexGrow: 1,
    },
    cardContainer: {
        padding: 0
    },
    container: {
        maxHeight: "57vh"
    },
    iconSize: {
        fontSize: "26px",
        marginBottom: 3,
        color: "white"
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
    contentWrap: {
        wordBreak: "break-word"
    }
}));

const GameType = {
    NCAA_WOMENS: 'ncaa_womens',
    NCAA_MENS: 'ncaa_mens',
    NBA: 'nba',
    WNBA: 'wnba',
}

const PeriodType = {
    QUARTER: 'Quarter',
    HALF: 'Half',
}

const getVideoUrl = (row) => row.OptimizedClipLocation || row.OriginalClipLocation;

// need to drop query params from key because params of pre-signed video urls are constantly changing
// and will break equality checks in memoization
const getVideoUrlKey = (row) => getVideoUrl(row).split("?")[0];

const propsAreEqual = (prevProps, nextProps) =>
    getVideoUrlKey(prevProps.row) === getVideoUrlKey(nextProps.row)
    && prevProps.eventStartTime === nextProps.eventStartTime
    && prevProps.onPlay === nextProps.onPlay
    && prevProps.onStop === nextProps.onStop
    && prevProps.handleDetailsView === nextProps.handleDetailsView;


function addSecondsToTime(startTime, n) {
    return moment(startTime).add(n, "seconds").format(" h:mm:ss A");
}


const handleDownload = async (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'video.mp4';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // try {
    //     const response = await query("get_blob", "api", videoUrl, {}, {}, "blob");

    //     if (!response.success || !response.data) {
    //     throw new Error(`Failed to fetch video: ${response.error || "Unknown error"}`);
    //     }

    //     const blob = response.data;
    //     downloadBlob(blob, filename);
    // } catch (error) {
    //     console.error("Error downloading video:", error);
    // }

};

// this is inefficient memory-wise but prevents videos from "reloading" when allClips is refreshed
const EventTableRow = React.memo(({ row, eventStartTime, onPlay, onStop, handleDetailsView }) => {
    const [videoError, setVideoError] = React.useState(false);
    const [retryCount, setRetryCount] = React.useState(0);
    const [isRetrying, setIsRetrying] = React.useState(false);

    const length = row.OptimizedClipLocation
        ? row.OptoLength
        : row.OriginalClipLocation
            ? row.OrigLength
            : "-";
    const videoUrl = getVideoUrl(row);

    // Reset error state when the base video URL changes (new video, not just new pre-signed URL)
    React.useEffect(() => {
        setVideoError(false);
        setRetryCount(0);
        setIsRetrying(false);
    }, [getVideoUrlKey(row)]);

    const handleVideoError = (error) => {
        setVideoError(true);

        // Auto-retry up to 3 times with exponential backoff
        if (retryCount < 3) {
            const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
            setIsRetrying(true);

            setTimeout(() => {
                setVideoError(false);
                setRetryCount(prev => prev + 1);
                setIsRetrying(false);
            }, delay);
        }
    };

    const handleVideoReady = () => {
        setVideoError(false);
    };

    return (
        <TableRow
            key={getVideoUrlKey(row)}
            data-index={videoUrl}
        >
            <TableCell align="center" style={{ width: 450 }}>
                {videoError ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '250px',
                        backgroundColor: '#000000',
                        borderRadius: '4px',
                        padding: '20px'
                    }}>
                        {isRetrying ? (
                            <>
                                <CircularProgress size={40} style={{ color: '#ffffff' }} />
                                <Typography variant="body2" style={{ marginTop: '10px', color: '#ffffff' }}>
                                    Retrying... (Attempt {retryCount + 1}/3)
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" style={{ color: '#999999' }}>
                                    Video not ready yet
                                </Typography>
                                {retryCount >= 3 && (
                                    <Typography variant="caption" style={{ marginTop: '5px', textAlign: 'center', color: '#999999' }}>
                                        The video file may still be processing. Try refreshing the page.
                                    </Typography>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <ReactPlayer
                        key={getVideoUrlKey(row)}  // Use stable key based on base URL
                        url={videoUrl}
                        width="100%"
                        height="100%"
                        controls
                        playsinline
                        style={{ objectFit: "cover" }}
                        onPlay={() => onPlay(videoUrl)}
                        onPause={() => onStop(videoUrl)}
                        onEnded={() => onStop(videoUrl)}
                        onError={handleVideoError}
                        onReady={handleVideoReady}
                    />
                )}
            </TableCell>
            <TableCell
                align="left"
                style={{ maxWidth: '350px', wordWrap: 'break-word'}}
                onClick={() => {
                    handleDetailsView(row)
                }}
            >
                <Button style={{ color: "#1976d2",fontWeight: 'bold', fontSize: "1.2rem" }}>{row.Label}</Button>
            </TableCell>
            <TableCell align="center">
                {addSecondsToTime(eventStartTime, row.StartTime)}
            </TableCell>
            <TableCell align="center">{length}</TableCell>
            <TableCell align='center'>
                <IconButton
                    onClick={() => {
                        // const url = row.OptimizedClipLocation ? row.OptimizedClipLocation : row.OriginalClipLocation;
                        const filename = row.Label? `${row.Label}.mp4`: 'video.mp4'
                        handleDownload(videoUrl, filename);
                    }}
                    aria-label="download"
                >
                    <GetAppIcon  />
                </IconButton>
            </TableCell>
        </TableRow>
    )
}, propsAreEqual);

export const EventView = () => {
    const classes = useStyles();
    const preventDefault = (event) => event.preventDefault();
    const navigate = useNavigate();
    const {state} = useLocation();

    const [rows, setRows] = useState([]);
    // const [visibleRows, setVisibleRows] = useState(new Set());
    // const rowRefs = useRef({});
    const [allClips, setAllClips] = React.useState([]);
    const [autoRefreshInterval, setAutoRefreshInterval] = React.useState(null);
    const [rowsPerPage, setRowsPerPage] = React.useState(CLIPS_LIMIT);
    const [page, setPage] = React.useState(0);
    const [profileViewDialogOpen, setProfileViewDialogOpen] = React.useState(false);
    const [profileClassifierState, setProfileClassifierState] = React.useState(undefined);
    const [selectedProfile, setSelectedProfile] = React.useState(false);
    const [nextToken, setNextToken] = React.useState(undefined);
    const [lastPage, setLastPage] = React.useState(0);
    const [isOptimizerConfiguredInProfile, setIsOptimizerConfiguredInProfile] = React.useState(false);
    const [isEventInfoOpen, setIsEventInfoOpen] = React.useState(true);
    const [playingVideos, setPlayingVideos] = React.useState({});
    const [rowUpdated, setRowUpdated] = React.useState(false);
    const [teams, setTeams] = React.useState([]);
    const [teamFilteredValues, setTeamFilteredValues] = React.useState([]);
    const [periodFilteredValues, setPeriodFilteredValues] = React.useState([]);
    const [maxPeriod, setMaxPeriod] = React.useState(1);
    const LazyReactPlayer = lazy(() => import("react-player"));
    const [timeStamp, setTimeStamp] = useState(undefined);

    const [periodType, setPeriodType] = useState(PeriodType.QUARTER);

    const {query, isLoading} = APIHandler();

    const stateParams = state;
    const eventData = _.get(stateParams, 'data')

    const goBack = () => {
        navigate("/listEvents");
    };

    if (!stateParams) {
        goBack();
    }

    const handleProfileViewDialogClose = () => {
        setProfileViewDialogOpen(false);
    }

    const handleProfileClick = (profileName) => {
        setSelectedProfile(profileName);
        setProfileViewDialogOpen(true);
    }

    // const downloadBlob = (blob, filename) => {
    //     const url = URL.createObjectURL(blob);
    //     const a = document.createElement("a");
    //     a.href = url;
    //     a.download = filename;

    //     const clickHandler = () => {
    //       setTimeout(() => {
    //         URL.revokeObjectURL(url);
    //         a.removeEventListener("click", clickHandler);
    //       }, 150);
    //     };

    //     a.addEventListener("click", clickHandler, false);
    //     document.body.appendChild(a);
    //     a.click();
    //     document.body.removeChild(a);
    //   };

    // useEffect(() => {
    //     const observer = new IntersectionObserver(
    //         (entries) => {
    //             entries.forEach((entry) => {
    //                 if (entry.isIntersecting) {
    //                     setVisibleRows((prev) => {
    //                         const newSet = new Set(prev);
    //                         newSet.add(entry.target.dataset.index);
    //                         return newSet;
    //                     });
    //                 }
    //             });
    //         },
    //         { threshold: 0.2 }
    //     );

    //     Object.values(rowRefs.current).forEach((row) => {
    //         if (row) observer.observe(row);
    //     });

    //     return () => observer.disconnect();
    // }, [rows]);

    // handle initial and "manual" data fetches (when filters or page number or rows per page change)
    React.useEffect(() => {
        (async () => {
            await fetchAllData();
            if (!rowUpdated) {
                setRowUpdated(true);
            }
        })();
    }, [teamFilteredValues, periodFilteredValues, page, rowsPerPage]);

    const cancelAutoRefresh = () => {
        if (autoRefreshInterval) {
            clearInterval(autoRefreshInterval);
        }
    }

    const canRefresh = _.isEmpty(playingVideos) && rowUpdated;
    const canAutoRefresh = canRefresh && eventData?.Status && EVENT_STATUSES_ENABLE_AUTO_REFRESH.includes(eventData?.Status);

    // handle auto refreshes
    React.useEffect(() => {
        if (canAutoRefresh) {
            setAutoRefreshInterval(
                setInterval(() => {
                    fetchAllData();
                }, config.autoRefreshTimeIntervals)
            )
        } else {
            cancelAutoRefresh();
        }
    }, [canAutoRefresh]);

    React.useEffect(() => {
        (async () => {
            setRows(getTableRows(allClips));
        })();
    }, [allClips]);

    const teamOptions = teams || [];

    const handleTeamFilterChange = (selected) => {
        setTeamFilteredValues(selected);
        setShouldRefresh(prev => !prev);
    };

    const periodOptions = Array.from({ length: maxPeriod }, (_, index) => ({
        value: (index + 1).toString(),
        label: `${periodType[0]} ${index + 1}`
    }));

    const handlePeriodFilterChange = (selected) => {
        setPeriodFilteredValues(selected);
        setShouldRefresh(prev => !prev);
    };

    const fetchAllData = async () => {
        const res = await fetchData();
        const metadata = await fetchEventMetadata();
        if (metadata) {
            eventData['Variables'] = metadata;
        }
        const filteredClips = res?.filter((clip) => clip !== null && clip?.OptoStartCode !== "Unsuccessful" && getVideoUrl(clip)) || [];
        setAllClips(filteredClips);
    }

    const fetchData = async () => {
        let retVal;

        let profileRes = await query('get', 'api', `profile/${eventData.Profile}`, {shouldContinueLoading: true});
        let profileClassifier = _.get(profileRes, 'data.Classifier.Name');
        setProfileClassifierState(profileClassifier);

        setIsOptimizerConfiguredInProfile(_.get(profileRes, 'data.Optimizer.Name') !== undefined ? true : false)

        if (profileClassifier) {
            let response = await query('get', 'api-data-plane', `event/${eventData.Name}/program/${eventData.Program}/profileClassifier/${profileClassifier}/track/1/segments/v2`,
                {
                    queryParams:
                    {
                        // limit: CLIPS_LIMIT
                        ...(teamFilteredValues?.length > 0 && { team: teamFilteredValues }),
                        ...(periodFilteredValues?.length > 0 && { period: periodFilteredValues}),

                    }
                }
            );

            retVal = _.get(response, 'data.Segments');
            setNextToken(response.LastEvaluatedKey ? JSON.stringify(response.LastEvaluatedKey) : "");
        }

        return retVal;
    };

    const fetchMoreData = async () => {
        let res = await query('get', 'api-data-plane', `event/${eventData.Name}/program/${eventData.Program}/profileClassifier/${profileClassifierState}/track/1/segments/v2`,
            {
                // limit: CLIPS_LIMIT,
                // "LastEvaluatedKey": nextToken
            }
        );

        if (res.success) {
            setAllClips(allClips.concat( _.get(res, 'data.Segments')));
            setRows(rows.concat(getTableRows( _.get(res, 'data.Segments'))));
            setNextToken(res.LastEvaluatedKey ? JSON.stringify(res.LastEvaluatedKey) : "");

            if (!res.LastEvaluatedKey) {
                setLastPage(page + 1)
            }
        }
    };

    const handleGameType = (contextVars) => {
        const gameType = contextVars.game_type ?? GameType.NCAA_WOMENS;
        const eventPeriodType = gameType === GameType.NCAA_MENS ? PeriodType.HALF : PeriodType.QUARTER;
        setPeriodType(eventPeriodType);
    }

    const fetchEventMetadata = async () => {
        let response = await query('get', 'api', `event/${eventData.Name}/program/${eventData.Program}/context-variables`, {disableLoader: true}, {ttl: 30000});

        setTeams([
            {
                value: response.data.home_team,
                label: response.data.home_team
            },
            {
                value: response.data.away_team,
                label: response.data.away_team
            }
        ]);
        handleGameType(response.data)
        return response.data?.data || undefined;
    }

    const handleRefresh = async () => {
        fetchAllData();
    };

    const handleChangePage = async (event, newPage) => {
        const rowsSize = _.size(rows);

        if (rowsSize > 0 && (newPage >= (rowsSize / rowsPerPage))) {
            // await fetchMoreData();
            setPage(page + 1);
        }
        else {
            setPage(newPage);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    // we don't want all changes to allClips, eventData, stateParams, and isOptimizerConfiguredInProfile
    // to trigger re-renders of all EventTableRows, so use a ref to pass this state into memoized handleDetailsView
    const handleDetailsViewRef = useRef({
        allClips,
        eventData,
        stateParams,
        isOptimizerConfiguredInProfile,
    });

    React.useEffect(() => {
        handleDetailsViewRef.current = {
            allClips,
            eventData,
            stateParams,
            isOptimizerConfiguredInProfile,
        }
    }, [allClips, eventData, stateParams, isOptimizerConfiguredInProfile]);

    const handleDetailsView = React.useCallback((clipInfo) => {
        navigate("/clipPreview", {state: {
                back: {
                    name: "View Event",
                    link: "/viewEvent"
                },
                clipdata: clipInfo,
                allClipsData: handleDetailsViewRef.current.allClips,
                origEventData: handleDetailsViewRef.current.eventData,
                mode: 'EventClips',
                data: _.get(handleDetailsViewRef.current.stateParams, 'data'),
                IsOptimizerConfiguredInProfile: handleDetailsViewRef.current.isOptimizerConfiguredInProfile
            }
        });
    }, []);

    const getTimeRemainingDuration = () => {
        let retVal;

        let now = moment().utc();
        let startTime = moment(eventData.Start).utc();
        let timeDiff = moment.duration(startTime.diff(now));

        if (startTime.isAfter(now)) {
            retVal = moment.duration(timeDiff, 'millisecond').format('d [days], h [hours], m [minutes], s [seconds]');
        }
        else {
            retVal = "Expired";
        }

        return retVal;
    };

    const navigateToReplay = (eventName, eventProgram) => {
        navigate("/listReplays", { state: {
                eventFilter: eventName,
                programFilter: eventProgram,
            }
        });
    };

    const navigateToStorylines = () => {
        navigate("/listStorylines", {
            state: {
                data: {
                    event: eventData,
                },
            },
        })
    }

    const getBucketUrl = (eventData) => {
        return `https://s3.console.aws.amazon.com/s3/buckets/${eventData.SourceVideoBucket}?prefix=${eventData.Program}/${eventData.Name}/${eventData.Profile}/`
    }

    const getClipCaption = (eventData) => {
        if (!eventData.GenerateOrigClips && !isOptimizerConfiguredInProfile){
            return "No optimizer set. Original Clip gen disabled"
        }
        else if (!eventData.GenerateOrigClips && !eventData.GenerateOptoClips){
            return "Original, Optimized segment clip generation disabled"
        }
        else if (!eventData.GenerateOrigClips && eventData.GenerateOptoClips){
            return "Original segment clip generation disabled"
        }
    }

    // using useCallback to prevent unnecessary EventTableRow re-renders
    const onPlay = React.useCallback((videoUrl) => {
        setPlayingVideos((playingVideos) => ({ ...playingVideos, [videoUrl]: true }));
    }, [setPlayingVideos]);

    // using useCallback to prevent unnecessary EventTableRow re-renders
    const onStop = React.useCallback((videoUrl) => {
        setPlayingVideos((playingVideos) => ({
            ...Object.fromEntries(
                Object.keys(playingVideos)
                .filter((key) => key !== videoUrl)
                .map(key => [key, true])),
            })
        );
    }, [setPlayingVideos]);

    const getTableRows = (rows) => rows?.map(row => {
        setMaxPeriod(prevMaxPeriod => Math.max(prevMaxPeriod, row.period));
        return (
            <EventTableRow
                key={getVideoUrlKey(row)}
                row={row}
                eventStartTime={eventData.Start}
                onPlay={onPlay}
                onStop={onStop}
                handleDetailsView={handleDetailsView}
            />
        )
    });

    if (!stateParams) {
        return (<></>);
    }
    else
        return (
            <>

                {selectedProfile && <ProfileViewDialog
                    onProfileViewDialogClose={handleProfileViewDialogClose}
                    open={profileViewDialogOpen}
                    profileName={selectedProfile}
                />}
                <Grid container direction="column" spacing={3} className={classes.content}>
                    <Grid item>
                        <Breadcrumbs>
                            <Link
                                style={{padding: 0}}
                                color="inherit"
                                component="button"
                                variant="subtitle2"
                                onClick={goBack}
                            >
                                {stateParams.back.name}
                            </Link>
                            <Typography color="textPrimary">{stateParams.data.Name}</Typography>
                        </Breadcrumbs>
                    </Grid>
                    <Grid item container direction="column" spacing={3}>
                        <Grid item container direction="row" justify="space-between">
                            <Grid item>
                                <Grid container item direction="row" spacing={2}>
                                    <Grid item>
                                        <Typography variant="h1" style={{maxWidth:"500px"}}>{stateParams.data.Name}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <CustomFilter
                                    options={teamOptions}
                                    onFilterChange={handleTeamFilterChange}
                                    multiSelect={false}
                                    label="Team"
                                    placeholder="Select Team"
                                />
                            </Grid>
                            <Grid item>
                                <CustomFilter
                                    options={periodOptions}
                                    onFilterChange={handlePeriodFilterChange}
                                    multiSelect={true}
                                    label={periodType}
                                    placeholder={`Select ${periodType}`}
                                />
                            </Grid>
                            <Grid item>
                                {canRefresh && (
                                    <IconButton size="small" onClick={handleRefresh}>
                                        <Tooltip title="Refresh">
                                            <AutorenewIcon className={classes.iconSize}/>
                                        </Tooltip>
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item container direction="row" justify="space-between" spacing={1}>
                            {isEventInfoOpen && <Grid container item direction="column" xs={2} spacing={2}>
                                {eventData.Status === "Complete" ?
                                    <Grid item>
                                        <EventStatsCard
                                            statValue={
                                                <Link
                                                    component="button"
                                                    style={{ fontWeight: 'bold' }}
                                                    onClick={() => navigateToStorylines()}
                                                >
                                                    View Storylines
                                                </Link>
                                            } />
                                    </Grid> :
                                    null
                                }
                                <Grid item>
                                    <EventStatsCard statValue={<Link component="button" style={{ fontWeight: 'bold' }} onClick={() => {
                                        navigateToReplay(eventData.Name, eventData.Program)
                                    }}>
                                        View Recaps
                                    </Link>}
                                    // statKey={"Recap Requests"}
                                    />
                                </Grid>
                                <Grid item>
                                    <EventStatsCard statValue={eventData.Status} statKey={"Status"}/>
                                </Grid>
                                <Grid item>
                                    <EventStatsCard statValue={_.size(rows)} statKey={"Clips"}/>
                                </Grid>
                                <Grid item>
                                    <EventStatsCard statValue={moment(eventData.Start).format('MM/DD/YY, h:mm A')}
                                                    statKey={"Start Time"}/>
                                </Grid>
                                <Grid item>
                                    <EventStatsCard statValue={moment.duration(eventData.DurationMinutes, "minutes").format("hh:mm:ss")}
                                                    statKey={"Duration"}/>
                                </Grid>
                            </Grid>}
                            <Grid container item direction="column" xs={10}>
                                <Card style={{paddingBottom: 0}}>
                                    <CardContent className={classes.cardContainer}>
                                        <Grid item container direction="column" spacing={3}>
                                            <Grid item>
                                                <Typography variant="h2" style={{paddingRight: 5, paddingLeft: 5}}>Key Moments</Typography>
                                            </Grid>
                                            <Grid item>
                                                <>
                                                    <TableContainer className={classes.container}>
                                                        <Table aria-label="clips table">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell align="center">Thumbnail</TableCell>
                                                                    <TableCell align="center" style={{minWidth:"200px"}}>Description</TableCell>
                                                                    <TableCell align="center">Start
                                                                        Time</TableCell>
                                                                    <TableCell align="center">Length
                                                                        (seconds)</TableCell>
                                                                    <TableCell align="left">Action</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {(isLoading && !rowUpdated) ? (
                                                                    <Backdrop open={true} className={classes.backdrop}>
                                                                        <CircularProgress color="inherit" />
                                                                    </Backdrop>
                                                                ) : (
                                                                    rows && rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                    <TablePagination
                                                        component="div"
                                                        labelDisplayedRows={() => {
                                                            return rows &&
                                                                `Page: ${page + 1} (${page * rowsPerPage + 1} - ${rows ? _.size(rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)) + page * rowsPerPage : 0}), Total Results: ${_.size(rows)}`;
                                                        }}
                                                        page={page}
                                                        onPageChange={handleChangePage}
                                                        rowsPerPage={rowsPerPage}
                                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                                        nextIconButtonProps={{
                                                            style: {
                                                                visibility: (page+1 === Math.ceil((_.size(rows || [])) / (rowsPerPage > 0 ? rowsPerPage : 1))) ? "hidden" : "visible"
                                                            }
                                                        }}
                                                    />
                                                    {
                                                        _.isEmpty(rows) && isLoading === false &&
                                                        <Grid container direction="row" item justify="center">
                                                            <Typography variant={"h6"}>
                                                                No segments detected for this Event.
                                                            </Typography>
                                                        </Grid>
                                                    }
                                                </>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </>

        )
};
